import React, { useEffect, useState, useContext, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Container,
  TableSortLabel,
  Button,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import { UserContext } from "../../context/UserContext";

const HISTORY_URL = `${process.env.REACT_APP_API_URL}/api/admin/history/report`;

const Reports = () => {
  const { user } = useContext(UserContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [orderBy, setOrderBy] = useState("branchName");
  const [order, setOrder] = useState("asc");

  const yellowButton = {
    background: "#FFD600",
    color: "#000000",
    fontWeight: "bold",
    "&:hover": {
      background: "#ffcc00",
      boxShadow: "0 4px 12px rgba(255, 214, 0, 0.5)",
    },
  };

  // Fetch Branch Report
  const fetchReport = useCallback(async () => {
    if (!user?.company) return;
    try {
      setLoading(true);
      const token = user.token;
      const res = await axios.get(HISTORY_URL, {
        headers: { Authorization: `Bearer ${token}` },
        params: { company: user.company },
      });

      if (res.data.status === "success") {
        setData(res.data.data);
      } else {
        console.error("Failed to fetch report:", res.data.message);
      }
    } catch (err) {
      console.error("Report fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Refresh data
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchReport();
    setRefreshing(false);
  };

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column);

    const sorted = [...data].sort((a, b) => {
      const aVal = a[column];
      const bVal = b[column];

      if (typeof aVal === "string") {
        return isAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return isAsc ? aVal - bVal : bVal - aVal;
    });
    setData(sorted);
  };

  // Compute grand total for numeric columns
  const grandTotal = data.reduce(
    (acc, row) => {
      acc.totalLogs += row.totalLogs || 0;
      acc.lessThan5 += row.lessThan5 || 0;
      acc.between5And15 += row.between5And15 || 0;
      acc.otherLogs += row.otherLogs || 0;
      return acc;
    },
    { totalLogs: 0, lessThan5: 0, between5And15: 0, otherLogs: 0 }
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f5f5", py: { xs: 1, sm: 2 } }}>
      <Container
        maxWidth={false}
        disableGutters
        sx={{
          width: { xs: "100%", md: "calc(100% - 96px)" },
          ml: { xs: 0, md: "84px" },
          mr: { xs: 0, md: "12px" },
          px: { xs: 1, sm: 2, md: 0.5 },
          boxSizing: "border-box",
        }}
      >
        {/* ===== BRANCH HISTORY REPORT TABLE ===== */}
        <Paper elevation={3} sx={{ p: { xs: 1.5, sm: 2 }, overflowX: "auto" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              mb: 2,
              gap: { xs: 1.5, sm: 0 },
            }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                color: "#000000",
                fontSize: { xs: "1rem", sm: "1.25rem" },
              }}
            >
              Network History Report
            </Typography>

            <IconButton
              onClick={handleRefresh}
              disabled={refreshing}
              size={isMobile ? "small" : "medium"}
              sx={{
                ...yellowButton,
                background: "transparent",
                border: "1px solid #FFD600",
                alignSelf: { xs: "flex-start", sm: "center" },
              }}
            >
              {refreshing ? (
                <CircularProgress size={isMobile ? 16 : 20} />
              ) : (
                <RefreshIcon fontSize={isMobile ? "small" : "medium"} />
              )}
            </IconButton>
          </Box>

          {/* Records Count */}
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              mb: 1,
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
            }}
          >
            Showing {data.length} record{data.length !== 1 ? "s" : ""}
          </Typography>

          {data.length === 0 ? (
            <Typography sx={{ textAlign: "center", py: 4, color: "#666666" }}>
              No branch history data available
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: "70vh", overflow: "auto" }}>
              <Table stickyHeader size={isMobile ? "small" : "medium"}>
                <TableHead>
                  <TableRow>
                    {[
                      { id: "branchName", label: "Branch Name" },
                      { id: "totalLogs", label: "Total Logs" },
                      { id: "lessThan5", label: "< 5 min" },
                      { id: "between5And15", label: "< 15 min" },
                      { id: "otherLogs", label: "15+ min" },
                    ].map((col) => (
                      <TableCell
                        align={col.id === "branchName" ? "left" : "center"}
                        key={col.id}
                        sx={{
                          fontWeight: "bold",
                          bgcolor: "#f5f5f5",
                          fontSize: { xs: "0.65rem", sm: "0.875rem" },
                          p: { xs: 0.5, sm: 1 },
                          whiteSpace: { xs: "nowrap", sm: "normal" },
                        }}
                      >
                        <TableSortLabel
                          active={orderBy === col.id}
                          direction={orderBy === col.id ? order : "asc"}
                          onClick={() => handleSort(col.id)}
                        >
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {data.map((branch, index) => (
                    <TableRow
                      key={branch.branchId || index}
                      hover
                      sx={{
                        "&:nth-of-type(odd)": {
                          backgroundColor: "#fafafa",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontSize: { xs: "0.65rem", sm: "0.875rem" },
                          p: { xs: 0.5, sm: 1 },
                          fontWeight: "medium",
                        }}
                      >
                        {branch.branchName}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontSize: { xs: "0.65rem", sm: "0.875rem" },
                          p: { xs: 0.5, sm: 1 },
                        }}
                      >
                        {branch.totalLogs}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontSize: { xs: "0.65rem", sm: "0.875rem" },
                          p: { xs: 0.5, sm: 1 },
                          color: branch.lessThan5 > 0 ? "#00e676" : "inherit",
                          fontWeight: branch.lessThan5 > 0 ? "bold" : "normal",
                        }}
                      >
                        {branch.lessThan5}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontSize: { xs: "0.65rem", sm: "0.875rem" },
                          p: { xs: 0.5, sm: 1 },
                          color: branch.between5And15 > 0 ? "#ff9800" : "inherit",
                          fontWeight: branch.between5And15 > 0 ? "bold" : "normal",
                        }}
                      >
                        {branch.between5And15}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontSize: { xs: "0.65rem", sm: "0.875rem" },
                          p: { xs: 0.5, sm: 1 },
                          color: branch.otherLogs > 0 ? "#ff4444" : "inherit",
                          fontWeight: branch.otherLogs > 0 ? "bold" : "normal",
                        }}
                      >
                        {branch.otherLogs}
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* GRAND TOTAL ROW */}
                  <TableRow
                    sx={{
                      fontWeight: "bold",
                      bgcolor: "#e0e0e0",
                      "&:hover": {
                        bgcolor: "#d5d5d5",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        fontSize: { xs: "0.65rem", sm: "0.875rem" },
                        p: { xs: 0.5, sm: 1 },
                      }}
                    >
                      <Typography
                        fontWeight={700}
                        sx={{
                          fontSize: { xs: "0.65rem", sm: "0.875rem" },
                        }}
                      >
                        Grand Total
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: "0.65rem", sm: "0.875rem" },
                        p: { xs: 0.5, sm: 1 },
                        fontWeight: "bold",
                      }}
                    >
                      {grandTotal.totalLogs}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: "0.65rem", sm: "0.875rem" },
                        p: { xs: 0.5, sm: 1 },
                        fontWeight: "bold",
                        color: grandTotal.lessThan5 > 0 ? "#00e676" : "inherit",
                      }}
                    >
                      {grandTotal.lessThan5}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: "0.65rem", sm: "0.875rem" },
                        p: { xs: 0.5, sm: 1 },
                        fontWeight: "bold",
                        color: grandTotal.between5And15 > 0 ? "#ff9800" : "inherit",
                      }}
                    >
                      {grandTotal.between5And15}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: { xs: "0.65rem", sm: "0.875rem" },
                        p: { xs: 0.5, sm: 1 },
                        fontWeight: "bold",
                        color: grandTotal.otherLogs > 0 ? "#ff4444" : "inherit",
                      }}
                    >
                      {grandTotal.otherLogs}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default Reports;